import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetMaterialImpactTotals extends Result {
  result: {
    material_impact: IImpact;
    company_impact: IImpact;
  };
}

export function useMaterialImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "materials", "materialImpactTotals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetMaterialImpactTotals>("/dashboard/materials/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export interface IMaterialImpactPerMaterial {
  id: number;
  name: string;
  suppliers: { name: string | null; address: string | null }[];
  unit: string;
  quantity: number;
  impact: IImpact;
}

export interface GetMaterialImpactPerMaterial extends Result {
  result: {
    total_rows: number;
    data: IMaterialImpactPerMaterial[];
    total_material_impact: IImpact;
  };
}

export type MaterialGroupBy = "id" | "name";

export function useMaterialImpactPerMaterial({
  pagination,
  filter,
  search,
  sort,
  metric,
  groupBy = "id",
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
  groupBy?: MaterialGroupBy;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "materials",
      "materialImpactPerMaterial",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      groupBy,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetMaterialImpactPerMaterial>("/dashboard/materials/per_material", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            group_by: groupBy,
            metric,
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export function useMaterialImpactPerMaterialExport({
  pagination,
  filter,
  search,
  sort,
  groupBy,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  groupBy?: MaterialGroupBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/materials/per_material", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            group_by: groupBy,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}

interface GetMaterialImpactTotalsOverTime extends Result {
  result: {
    material_impact: ({ year: number; month: number } & IImpact)[];
  };
}

export function useMaterialImpactTotalsOverTime() {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "materials", "materialImpactTotalsOverTime", startDate, endDate],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetMaterialImpactTotalsOverTime>("/dashboard/materials/totals_over_time", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}
